import React, { useState, useRef, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Container,
  Card,
  FormCheck,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import DatePicker from "react-multi-date-picker";
import { RxCalendar } from "react-icons/rx";
import IconInfo from "../../../images/dashboard/icon-info.png";
import DocumentUpload from "../../../components/DocumentUpload";
import DownloadSampleFile from "../../../components/DownloadSampleFile";
import axios from "axios";
import apiConfig from "../../credentials/apiConfig";
import Loader from "../../../components/Loader";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import image from "../../../images/ai-robot.gif";

const Setup = ({
  onStepComplete,
  engagementId,
  engagementYear,
  currentYearTrialBalance,
  priorYearTrialBalance,
  priorYearFinancials,
  annualize,
  handleMapping,
  engagementData,
  engagementLoading,
   onEngagementDatesUpdate,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [isMapping, setIsMapping] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [datesModifiedByUser, setDatesModifiedByUser] = useState({
    startDate: false,
    endDate: false
  });

  // Add state to keep latest file URLs
  const [latestFileUrls, setLatestFileUrls] = useState({
    currentYearTrialBalance: currentYearTrialBalance || null,
    priorYearTrialBalance: priorYearTrialBalance || null,
    priorYearFinancials: priorYearFinancials || null,
  });

  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  // Parse dates from API
  const parseUTCDate = (dateString) => {
    if (!dateString) return null;

    // Parse the UTC date but preserve the actual date components
    // Split the ISO string and get just the date part (YYYY-MM-DD)
    const dateParts = dateString.split('T')[0].split('-');
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(dateParts[2], 10);

    // Create date with local timezone but preserve the date components
    const localDate = new Date(year, month, day);
    return localDate;
  };


  // Update file URLs whenever engagementData changes
  useEffect(() => {
    if (engagementData && !engagementLoading) {   
      // Get default dates based on engagement year
      const engYear = engagementData.engagementYear || engagementYear || new Date().getFullYear();
      const { startDate: defaultStart, endDate: defaultEnd } = getCorrectEngagementDates(engYear);

      // IMPORTANT: Use API dates if available, otherwise use defaults
      const effectiveStartDate = engagementData.startDate ? parseUTCDate(engagementData.startDate) : defaultStart;
      const effectiveEndDate = engagementData.endDate ? parseUTCDate(engagementData.endDate) : defaultEnd;



      // Always set dates in latestFileUrls, whether from API or defaults
      setLatestFileUrls({
        currentYearTrialBalance: engagementData.currentYearTrialBalance || null,
        priorYearTrialBalance: engagementData.priorYearTrialBalance || null,
        priorYearFinancials: engagementData.priorYearFinancials || null,
        startDate: effectiveStartDate,
        endDate: effectiveEndDate
      });

      // Force Formik to reinitialize
      setResetKey(prev => prev + 1);
    }
  }, [engagementData, engagementLoading, engagementYear]);

  // Add effect to fetch latest engagement data when this component mounts or when returned to
  useEffect(() => {
    if (engagementId) {
      refreshEngagementFiles();
    }
  }, [engagementId]);

  // Function to refresh engagement files from API
  const refreshEngagementFiles = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      const res = await axios.get(`${apiConfig.apiBaseURL}/getEngagementById/${engagementId}`, {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.success && res.data.engagement) {


        // For existing engagements with dates
        if (res.data.engagement.startDate && res.data.engagement.endDate) {
          const apiStartDate = parseUTCDate(res.data.engagement.startDate);
          const apiEndDate = parseUTCDate(res.data.engagement.endDate);


          // Update the latest file URLs and dates
          setLatestFileUrls({
            currentYearTrialBalance: res.data.engagement.currentYearTrialBalance || null,
            priorYearTrialBalance: res.data.engagement.priorYearTrialBalance || null,
            priorYearFinancials: res.data.engagement.priorYearFinancials || null,
            startDate: apiStartDate,
            endDate: apiEndDate
          });
        }
        // For new engagements with no dates
        else {
          // Get the engagement year
          const engYear = res.data.engagement.engagementYear || engagementYear;
          const { startDate: defaultStart, endDate: defaultEnd } = getCorrectEngagementDates(engYear);

          // Update with default dates based on engagement year
          setLatestFileUrls({
            currentYearTrialBalance: res.data.engagement.currentYearTrialBalance || null,
            priorYearTrialBalance: res.data.engagement.priorYearTrialBalance || null,
            priorYearFinancials: res.data.engagement.priorYearFinancials || null,
            startDate: defaultStart,
            endDate: defaultEnd
          });
        }

        // Force Formik to reinitialize
        setResetKey(prev => prev + 1);
      }
    } catch (err) {
      console.error("Error refreshing engagement files:", err);
    }
  };

  const getCorrectEngagementDates = (engagementYr) => {
    // Parse year to ensure it's a number
    const year = parseInt(engagementYr, 10);

    if (isNaN(year)) {
      const currentYear = new Date().getFullYear();
      const startDate = new Date(currentYear, 0, 1);
      const endDate = new Date(currentYear, 11, 31);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return { startDate, endDate };
    }

    const startDate = new Date(year, 0, 1);     // January 1st of engagement year
    const endDate = new Date(year, 11, 31);     // December 31st of engagement year
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  };

  // Get default dates based on the engagement year
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCorrectEngagementDates(engagementYear);

  // Initial form values - use the latest file URLs
  const initialValues = {
    startDate: latestFileUrls.startDate || defaultStartDate,
    endDate: latestFileUrls.endDate || defaultEndDate,
    annualize: annualize || false,
    currentYearTrialBalance: latestFileUrls.currentYearTrialBalance || null,
    priorYearTrialBalance: latestFileUrls.priorYearTrialBalance || null,
    priorYearFinancials: latestFileUrls.priorYearFinancials || null,
  };

  // Allowed file types for each document
  const FILE_FORMATS = {
    currentYearTrialBalance: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ],
    priorYearTrialBalance: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ],
    priorYearFinancials: [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  };

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    startDate: Yup.date().required("Start Date is required"),
    endDate: Yup.date()
      .required("End Date is required")
      .test('is-after-start', 'End Date must be after the Start Date', function (endDate) {
        const { startDate } = this.parent;
        if (!startDate || !endDate) return true; // Skip validation if either date is missing

        // Normalize dates for comparison (remove time portion)
        const startDateString = new Date(startDate).toISOString().split('T')[0];
        const endDateString = new Date(endDate).toISOString().split('T')[0];

        // Compare only the date portions (YYYY-MM-DD)
        return endDateString >= startDateString;
      }),
    currentYearTrialBalance: Yup.mixed()
      .required("Current Year Trial Balance is required")
      .test("fileType", "Invalid file format", (file) => {
        if (!file) return false; // Required
        if (typeof file === "string") return true; // Assume valid if it's a URL
        return FILE_FORMATS.currentYearTrialBalance.includes(file.type);
      }),
    priorYearTrialBalance: Yup.mixed()
      .required("Prior Year Trial Balance is required")
      .test("fileType", "Invalid file format", (file) => {
        if (!file) return false; // Required
        if (typeof file === "string") return true; // Assume valid if it's a URL
        return FILE_FORMATS.priorYearTrialBalance.includes(file.type);
      }),
    priorYearFinancials: Yup.mixed()
      .required("Prior Year Financials is required")
      .test("fileType", "Invalid file format", (file) => {
        if (!file) return false; // Required
        if (typeof file === "string") return true; // Assume valid if it's a URL
        return FILE_FORMATS.priorYearFinancials.includes(file.type);
      }),
  });

  const formatDateForAPI = (date) => {
    if (!date) return null;

    // Extract date components in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Return in YYYY-MM-DD format (which backend expects)
    return `${year}-${month}-${day}`;
  };

  const formatDateForProps = (date) => {
    if (!date) return null;

    // First format as YYYY-MM-DD 
    const formattedDate = formatDateForAPI(date);

    // Then convert to ISO format with time (needed for downstream components)
    // This creates a proper ISO string with the date at midnight UTC
    return formattedDate ? `${formattedDate}T00:00:00.000Z` : null;
  };

  // Improved date change handler - correctly handles DatePicker values
  const handleDateChange = (date, fieldName, setFieldValue, formValues) => {
    // Create a proper Date object from the DatePicker selection
    let selectedDate;

    if (date instanceof Date) {
      // Already a Date object
      selectedDate = new Date(date);
    } else {
      // DatePicker might return a special object or string
      try {
        const dateStr = date.toString();
        // Check if it's in MM/DD/YYYY format
        if (dateStr.indexOf('/') > -1) {
          const [month, day, year] = dateStr.split('/').map(num => parseInt(num, 10));
          selectedDate = new Date(year, month - 1, day);
        } else {
          // Assume it's the DatePicker object with year, month, day properties
          const year = date.year || date.getFullYear();
          const month = (date.month ? date.month - 1 : date.getMonth());
          const day = date.day || date.getDate();
          selectedDate = new Date(year, month, day);
        }
      } catch (err) {
        console.error("Error parsing date:", err);
        // Fallback to current date
        selectedDate = new Date();
      }
    }

    // Ensure time is midnight in local timezone
    selectedDate.setHours(0, 0, 0, 0);
    setFieldValue(fieldName, selectedDate);

    // Mark this date as modified by the user
    setDatesModifiedByUser(prev => ({
      ...prev,
      [fieldName]: true
    }));

    // If this is the start date and we want to enforce end date to be Dec 31 of that year
    if (fieldName === "startDate" && formValues.annualize) {
      const year = parseInt(engagementYear);
      const endDate = new Date(year, 11, 31);
      endDate.setHours(0, 0, 0, 0);
      setFieldValue("endDate", endDate);

      // Also mark endDate as modified since it was automatically adjusted
      setDatesModifiedByUser(prev => ({
        ...prev,
        endDate: true
      }));
    }
  };

  const processSubmit = async (values) => {
    const authToken = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("engagementId", engagementId || "");

    // Format dates consistently for the API
    formData.append("startDate", formatDateForAPI(values.startDate));
    formData.append("endDate", formatDateForAPI(values.endDate));

    // Convert boolean to string
    formData.append("annualize", values.annualize);

    // Append file fields - handle both file objects and URLs
    if (typeof values.currentYearTrialBalance === "string") {
      formData.append(
        "currentYearTrialBalanceUrl",
        values.currentYearTrialBalance
      );
    } else {
      formData.append(
        "currentYearTrialBalance",
        values.currentYearTrialBalance
      );
    }

    if (typeof values.priorYearTrialBalance === "string") {
      formData.append("priorYearTrialBalanceUrl", values.priorYearTrialBalance);
    } else {
      formData.append("priorYearTrialBalance", values.priorYearTrialBalance);
    }

    if (typeof values.priorYearFinancials === "string") {
      formData.append("priorYearFinancialsUrl", values.priorYearFinancials);
    } else {
      formData.append("priorYearFinancials", values.priorYearFinancials);
    }

    const response = await axios.post(
      `${apiConfig.apiBaseURL}/engagementMapping`,
      formData,
      {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return response;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSaveLoading(true);
      const response = await processSubmit(values);
      if (response.data.success) {
        // Update local state with the latest file URLs
        setLatestFileUrls({
          currentYearTrialBalance: response.data.currentYearTrialBalance || values.currentYearTrialBalance,
          priorYearTrialBalance: response.data.priorYearTrialBalance || values.priorYearTrialBalance,
          priorYearFinancials: response.data.priorYearFinancials || values.priorYearFinancials,
        });

        toast.success("Data saved successfully!");
        navigate("/engagements");
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      const errorMessage =
        error.response?.data?.message ||
        "No response received from server. Please check your network connection.";
      toast.error(errorMessage);
    } finally {
      setSaveLoading(false);
      setSubmitting(false);
    }
  };

  const continueMapping = async (values, formikHelpers) => {
    try {
      setLoading(true);
      setMappingLoading(true);
      setIsMapping(true);

      const formData = new FormData();

      formData.append("engagementId", engagementId);
      formData.append("annualize", values.annualize);

      // Format dates consistently for the API
      if (values.startDate) {
        formData.append("startDate", formatDateForAPI(values.startDate));
      }

      if (values.endDate) {
        formData.append("endDate", formatDateForAPI(values.endDate));
      }

      formData.append("mode", "map");

      // Handle both file objects and URLs
      if (typeof values.currentYearTrialBalance === "string") {
        formData.append(
          "currentYearTrialBalanceUrl",
          values.currentYearTrialBalance
        );
      } else if (values.currentYearTrialBalance instanceof File) {
        formData.append(
          "currentYearTrialBalance",
          values.currentYearTrialBalance
        );
      }

      if (typeof values.priorYearTrialBalance === "string") {
        formData.append(
          "priorYearTrialBalanceUrl",
          values.priorYearTrialBalance
        );
      } else if (values.priorYearTrialBalance instanceof File) {
        formData.append("priorYearTrialBalance", values.priorYearTrialBalance);
      }

      if (typeof values.priorYearFinancials === "string") {
        formData.append("priorYearFinancialsUrl", values.priorYearFinancials);
      } else if (values.priorYearFinancials instanceof File) {
        formData.append("priorYearFinancials", values.priorYearFinancials);
      }

      const mappingResponse = await axios.post(
        `${apiConfig.apiBaseURL}/engagementMapping`,
        formData,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (mappingResponse.data.success) {
        const responseData = mappingResponse.data;

        // Format start and end dates for downstream components
        if (values.startDate) {
          responseData.startDate = formatDateForProps(values.startDate);
        }

        if (values.endDate) {
          responseData.endDate = formatDateForProps(values.endDate);
        }

        // ✅ Detect whether user changed default dates
        const defaultDates = getCorrectEngagementDates(engagementYear);

        const userActuallyModifiedStart =
          datesModifiedByUser.startDate ||
          formatDateForAPI(values.startDate) !== formatDateForAPI(defaultDates.startDate);

        const userActuallyModifiedEnd =
          datesModifiedByUser.endDate ||
          formatDateForAPI(values.endDate) !== formatDateForAPI(defaultDates.endDate);

  // Call the parent callback to update engagement dates
  if (onEngagementDatesUpdate) {
    onEngagementDatesUpdate({
      startDate: responseData.startDate,
      endDate: responseData.endDate,
      startDateModified: userActuallyModifiedStart,
      endDateModified: userActuallyModifiedEnd,
      annualize: values.annualize
    });
  }

  // Continue with existing logic...
  onStepComplete();
  handleMapping();
} else {
        throw new Error(mappingResponse.data.message || "Mapping failed");
      }
    } catch (error) {
      console.error("Mapping error:", error);

      const errorMessage =
        error.response?.data?.message || // backend error message
        error.message || // JS error
        "An unexpected error occurred";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setMappingLoading(false);
      setIsMapping(false);
    }
  };

  if (loading && isMapping) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          zIndex: 1050,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "420px",
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: "40px 20px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            textAlign: "center",
          }}
        >
          <img
            src={image}
            alt="Processing"
            style={{
              width: "120px",
              marginBottom: "20px",
            }}
          />
          <h5 style={{ fontWeight: 500, fontSize: "18px", color: "#333" }}>
            The AI is working on it.
          </h5>
          <p style={{ fontSize: "14px", color: "#666" }}>
            This might take a little time, so please be patient.
          </p>
        </div>
      </div>
    );
  }

  const getCleanFileName = (fileKeyOrUrl) => {
    if (!fileKeyOrUrl) return "";

    try {
      const fileName = decodeURIComponent(fileKeyOrUrl.split('/').pop().split('?')[0]);
      // Remove numeric prefix followed by hyphen (e.g., "1747722369971-")
      return fileName.replace(/^\d+-/, '');
    } catch (e) {
      return fileKeyOrUrl;
    }
  };


  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        key={resetKey} // Force Formik to reinitialize when file URLs change
      >
        {(formik) => {
          const {
            setFieldValue,
            values,
            isSubmitting,
            isValid,
            errors,
            touched,
          } = formik;

          // Check if form is valid and all required fields are filled
          const isFormComplete =
            isValid &&
            values.startDate &&
            values.endDate &&
            values.currentYearTrialBalance &&
            values.priorYearTrialBalance &&
            values.priorYearFinancials;

          return (
            <Form className="custom-inputs mt-4">
              <Container fluid className="mx-0">
                <Card className="py-3" style={{ border: "2px solid #ECEEF5" }}>
                  <Row className="px-4 py-2">
                    <h5 className="fw-bold text-dark mb-4">
                      Engagement Period
                    </h5>
                    {/* Start Date */}
                    <Col md={6}>
                      <label>Start Date*</label>
                      <div className="d-flex align-items-center position-relative">
                        <DatePicker
                          value={values.startDate}
                          disabled={values.annualize}
                          onChange={(date) => handleDateChange(date, "startDate", setFieldValue, values)}
                          format="MM/DD/YYYY"
                          placeholder="Select Start Date"
                          className={`form-control ${touched.startDate && errors.startDate
                            ? "is-invalid"
                            : ""
                            }`}
                          ref={startDatePickerRef}
                        />
                        <RxCalendar
                          className="position-absolute end-0 top-50 translate-middle-y me-2 text-light"
                          size="20px"
                          onClick={() => {
                            if (startDatePickerRef.current) {
                              startDatePickerRef.current.openCalendar();
                            }
                          }}
                        />
                      </div>
                      <ErrorMessage
                        name="startDate"
                        component="div"
                        className="text-danger"
                      />
                    </Col>

                    {/* End Date */}
                    <Col md={6}>
                      <label>End Date*</label>
                      <div className="d-flex align-items-center position-relative">
                        <DatePicker
                          value={values.endDate}
                          disabled={values.annualize}
                          onChange={(date) => handleDateChange(date, "endDate", setFieldValue, values)}
                          format="MM/DD/YYYY"
                          placeholder="Select End Date"
                          className={`form-control ${touched.endDate && errors.endDate
                            ? "is-invalid"
                            : ""
                            }`}
                          ref={endDatePickerRef}
                        />
                        <RxCalendar
                          className="position-absolute end-0 top-50 translate-middle-y me-2 text-light"
                          size="20px"
                          onClick={() => {
                            if (endDatePickerRef.current) {
                              endDatePickerRef.current.openCalendar();
                            }
                          }}
                        />
                      </div>
                      <ErrorMessage
                        name="endDate"
                        component="div"
                        className="text-danger"
                      />
                    </Col>

                    <Col className="d-flex mt-1">
                      <FormCheck
                        type="checkbox"
                        checked={values.annualize}
                        onChange={(e) => {
                          setFieldValue("annualize", e.target.checked);
                        }}
                        className="me-2"
                      />
                      <label className="mt-1">
                        Do you want to <b>Annualize?</b>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="annualize-tooltip">
                              Annualizing allows you to project full-year
                              results from partial-year data.
                            </Tooltip>
                          }
                        >
                          <img
                            src={IconInfo}
                            alt="info"
                            style={{ marginLeft: "15px", cursor: "pointer" }}
                          />
                        </OverlayTrigger>
                      </label>
                    </Col>
                  </Row>

                  <hr />

                  <Row className="px-4 pt-2 ">
                    <h5 className="fw-bold text-dark mb-4">
                      Required Documents
                    </h5>
                    <Col lg={4}>
                      <DocumentUpload
                        key={`current-${resetKey}`}
                        formik={formik}
                        name="currentYearTrialBalance"
                        acceptedTypes={{
                          "application/vnd.ms-excel": [],
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
                          "text/csv": [],
                        }}
                        label="Current Year Trial Balance*"
                        // fileUrl={latestFileUrls.currentYearTrialBalance}
                        fileUrl={getCleanFileName(latestFileUrls.currentYearTrialBalance)}
                      />
                      {/* <DownloadSampleFile
                        fileName="Trial_Balance_Template.xlsx"
                        displayText="Sample Current Year Trial Balance"
                      /> */}
                      {!formik.values.currentYearTrialBalance && (
                        <DownloadSampleFile
                          fileName="Trial_Balance_Template.xlsx"
                          displayText="Sample Current Year Trial Balance"
                        />
                      )}
                    </Col>
                    <Col lg={4}>
                      <DocumentUpload
                        key={`priorTrial-${resetKey}`}
                        formik={formik}
                        name="priorYearTrialBalance"
                        acceptedTypes={{
                          "application/vnd.ms-excel": [],
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
                          "text/csv": [],
                        }}
                        label="Prior Year Trial Balance*"
                        // fileUrl={latestFileUrls.priorYearTrialBalance}
                        fileUrl={getCleanFileName(latestFileUrls.priorYearTrialBalance)}
                      />
                      {!formik.values.priorYearTrialBalance && (
                        <DownloadSampleFile
                          fileName="Trial_Balance_Template.xlsx"
                          displayText="Sample Prior Year Trial Balance"
                        />
                      )}
                    </Col>

                    {/* Prior Year Financials */}
                    <Col lg={4}>
                      <DocumentUpload
                        key={`priorFinancial-${resetKey}`}
                        formik={formik}
                        name="priorYearFinancials"
                        acceptedTypes={{
                          "application/pdf": [],
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
                        }}
                        label="Prior Year Financials*"
                        // fileUrl={latestFileUrls.priorYearFinancials}
                        fileUrl={getCleanFileName(latestFileUrls.priorYearFinancials)}
                      />
                    </Col>
                  </Row>
                </Card>
              </Container>
              <Container fluid className="px-0 setup-footer">
                <Card
                  className="h-100 py-4 px-3 mt-3"
                  style={{ border: "2px solid #ECEEF5" }}
                >
                  <div className="d-flex justify-content-between mt-3 all-buttons">
                    <div className="left">
                      <Button
                        type="submit"
                        variant="outline-primary"
                        className="px-5 py-3 me-3"
                        disabled={isSubmitting || loading || mappingLoading}
                      >
                        {saveLoading ? "Saving..." : "Save & Exit"}
                      </Button>
                    </div>
                    <div className="right">
                      {/* <Button
                        variant="outline-primary"
                        className="px-5 py-3 me-3"
                        onClick={goToDocumentSummary}
                        disabled={mappingLoading || saveLoading}
                      >
                        Skip to Document Summary
                      </Button> */}

                      <Button
                        type="button"
                        variant="primary"
                        className="px-5 py-3"
                        onClick={() => {
                          // Validate form before proceeding to mapping
                          formik.validateForm().then((errors) => {
                            if (Object.keys(errors).length === 0) {
                              continueMapping(values, formik);
                            } else {
                              // Touch all fields to show validation errors
                              Object.keys(formik.values).forEach((field) => {
                                formik.setFieldTouched(field, true);
                              });
                              toast.error(
                                "Please fill all required fields correctly before continuing"
                              );
                            }
                          });
                        }}
                        disabled={mappingLoading || saveLoading || !isFormComplete}
                      >
                        {mappingLoading
                          ? "Processing..."
                          : "Continue to Mapping"}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Container>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default Setup;